import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './lib/auth-context';
import { DashboardLayout } from './components/DashboardLayout';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { OAuthAuthorizePage } from './pages/OAuthAuthorizePage';
import { WelcomePage } from './pages/WelcomePage';
import { OverviewPage } from './pages/OverviewPage';
import { McpSetupPage } from './pages/McpSetupPage';
import { UsagePage } from './pages/UsagePage';
import { OAuthClientsPage } from './pages/OAuthClientsPage';
import { BillingPage } from './pages/BillingPage';
import { AlertsPage } from './pages/AlertsPage';
import { SettingsPage } from './pages/SettingsPage';
import { IntelligenceSourcesPage } from './pages/IntelligenceSourcesPage';
import { IntelligenceTrendsPage } from './pages/IntelligenceTrendsPage';
import { IntelligenceDocumentsPage } from './pages/IntelligenceDocumentsPage';
import { IntelligenceSimilarityPage } from './pages/IntelligenceSimilarityPage';
import { IntelligenceGraphPage } from './pages/IntelligenceGraphPage';
import { IntelligenceInspectorsPage } from './pages/IntelligenceInspectorsPage';
import { supabaseConfigError } from './lib/supabase';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function App() {
  if (supabaseConfigError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
        <div className="w-full max-w-xl rounded-xl border border-amber-200 bg-white p-8 shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-wide text-amber-700">Configuration Error</p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900">Dashboard setup is incomplete</h1>
          <p className="mt-4 text-sm leading-6 text-slate-600">{supabaseConfigError}</p>
          <p className="mt-3 text-sm leading-6 text-slate-600">
            For local development, add SUPABASE_URL and SUPABASE_ANON_KEY to the repo root .env file, then restart the dev server.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      {/* Not inside ProtectedRoute: the page performs its own session check so it
          can preserve the full OAuth query string in a returnTo redirect. */}
      <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
      <Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<OverviewPage />} />
        <Route path="mcp-setup" element={<McpSetupPage />} />
        <Route path="usage" element={<UsagePage />} />
        <Route path="oauth-clients" element={<OAuthClientsPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="intelligence" element={<Navigate to="/intelligence/sources" replace />} />
        <Route path="intelligence/sources" element={<IntelligenceSourcesPage />} />
        <Route path="intelligence/trends" element={<IntelligenceTrendsPage />} />
        <Route path="intelligence/documents" element={<IntelligenceDocumentsPage />} />
        <Route path="intelligence/similarity" element={<IntelligenceSimilarityPage />} />
        <Route path="intelligence/graph" element={<IntelligenceGraphPage />} />
        <Route path="intelligence/inspectors" element={<IntelligenceInspectorsPage />} />
      </Route>
    </Routes>
  );
}
